"use client";

import React from "react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
function SmartHomeTipsConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 text-center">
        <AlertCircle className="size-10 text-destructive" />
        <p className="text-lg font-medium text-foreground">Failed to load tips.</p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex min-h-[400px] items-center justify-center text-center">
        <p className="text-lg text-muted-foreground">No smart home tips available at the moment.</p>
      </div>;
  }
  return <div className="w-full">
      <div className="columns-1 gap-8 md:columns-2 lg:columns-3 lg:gap-[120px]">
        {posts.map((post: Post, i: number) => {
        const category = post.customFields?.category || "General";
        const imageUrl = post.featuredImage || post.customFields?.image || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/generic_smart_home_placeholder.png";
        return <Link key={post.id} to={`/post/smart_home_tip/${post.id}`} data-index={i} className="group mb-8 block break-inside-avoid outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:mb-[120px]">
              <Card className="overflow-hidden rounded-lg border-none bg-card p-0 shadow-md transition-shadow duration-500 hover:shadow-lg">
                <div className="relative overflow-hidden bg-muted">
                  <img src={imageUrl} alt={post.title} className="h-auto w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" loading="lazy" />
                </div>

                {post.customFields?.body_image && <div className="px-10 pt-6">
                    <img src={post.customFields.body_image} alt={post.title || "Body Image"} className="h-auto w-full rounded-md object-cover" loading="lazy" />
                  </div>}

                <div className="flex flex-col gap-3 p-10">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {category}
                    </span>
                    {post.customFields?.author_name && <span className="text-xs font-medium text-muted-foreground/80">
                        Author: {post.customFields.author_name}
                      </span>}
                  </div>
                  <h3 className="font-default text-2xl font-semibold tracking-tight text-foreground">
                    {post.title}
                  </h3>
                  {post.customFields?.tip_content && <div className="mt-2 space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        Tip Content:
                      </span>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                        {post.customFields.tip_content}
                      </p>
                    </div>}
                </div>
              </Card>
            </Link>;
      })}
      </div>

      {hasNext && <div className="mt-12 flex justify-center lg:mt-8">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="min-w-[200px] rounded-full">
            {isRefetching ? <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Loading...
              </> : "Load More Tips"}
          </Button>
        </div>}
    </div>;
}
export default function SmartHomeTipsListing() {
  const wp_query_params = {
    post_type: "smart_home_tip",
    per_page: 100,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="smart-home-tips-listing" className="bg-background px-6 py-24 md:px-12 lg:px-24 lg:py-32">
      <div className="mx-auto max-w-[1800px]">
        <PostsProvider wp_query={{
        post_type: "smart_home_tip",
        per_page: 100,
        status: ["publish"]
      }}>
          <SmartHomeTipsConsumer />
        </PostsProvider>
      </div>
    </section>;
}